/**
 * Embeds the consumer's already-loaded `@font-face` rules for a given font
 * family directly into generated SVG markup, as base64 `data:` URIs.
 *
 * Bugreport 2026-08-31 (Stefan, Linux): the textbox view-mode raster
 * (`textbox-svg-renderer.ts`) is an `<img>` decoding a `data:image/svg+xml`
 * URL — a browser-spec "restricted image context" that does NOT reliably
 * share the parent document's `@font-face` cache across every browser/OS
 * combination. Confirmed: on Linux/Chrome the raster fell back to Ubuntu
 * (its default sans-serif) even though D-DIN had already fully loaded on the
 * page and `document.fonts.ready` had resolved — waiting for that promise
 * (see `textbox.renderer.ts`) fixes the TIMING half of the bug but not this
 * one. The isolated image context needs the font EMBEDDED in the SVG
 * document itself, not just loaded on the page — the same technique DOM/SVG
 * rasterization libraries (html2canvas, dom-to-image) use for the identical
 * limitation.
 *
 * This scans the consumer's own stylesheets (`document.styleSheets`) for
 * `@font-face` rules whose `font-family` matches, fetches each referenced
 * font file, and returns a `<style>@font-face{...}</style>` string with the
 * font embedded as a `data:` URI — self-contained, so the SVG-image context
 * can resolve it without depending on anything external or on browser/OS
 * font-sharing behaviour. A SUCCESSFUL result is cached for the module's
 * lifetime (font files don't change at runtime) — every raster after the
 * first reuses the cached string for free, no repeated fetches. A
 * failed/empty result is only cached briefly (see `NEGATIVE_CACHE_TTL_MS`
 * below), not for the module's lifetime — a later raster still retries.
 *
 * Consumer-agnostic by design (package philosophy: "board house font, system
 * fallback" — see `STICKY_FONT_FAMILY`): if the consumer never declared an
 * `@font-face` for the family (or a stylesheet is unreadable — cross-origin —
 * or a fetch fails), this resolves to an empty string and the raster falls
 * back to the system font exactly as before, matching the documented
 * fallback contract rather than throwing.
 */

const cache = new Map<string, Promise<string>>();

/**
 * Review-Lauf 01 (MAJOR, Linse A/B): the `fetch` below had no timeout — a
 * hung request (slow network, CDN outage) used to block not just the
 * current raster but EVERY future textbox raster of the whole session,
 * since the pending Promise was cached before resolution. Bounded here so a
 * stuck fetch fails (and, per the cache fix below, can be retried) instead
 * of hanging forever.
 */
const FETCH_TIMEOUT_MS = 8000;

/**
 * Review-Lauf 02 (MAJOR, Linse B): evicting a failed/empty result
 * IMMEDIATELY (Lauf 01's fix) reopened a different problem — every raster
 * request arriving right after the eviction (e.g. several textboxes
 * re-rastering during the SAME zoom gesture, whose trailing-raster debounce
 * is far shorter than `FETCH_TIMEOUT_MS`) starts its own independent, up to
 * 8s fetch attempt against the same URL. During a multi-second CDN
 * slowdown/outage that could stack up several overlapping long-lived
 * fetches and, given the browser's per-origin connection limit, crowd out
 * other whiteboard network activity (images, API calls) — in exactly the
 * degraded-network situation the fix was meant to make robust. A short
 * negative-cache window keeps a failed result available to near-term
 * callers (no fetch storm) while still remaining short enough that the
 * "never permanently blocked" guarantee from Lauf 01 holds.
 */
const NEGATIVE_CACHE_TTL_MS = 3000;

/** Pending eviction timers for cached failures, keyed by family — tracked so
 *  {@link __resetEmbeddedFontFaceCssCacheForTests} can clear them and tests
 *  don't leak timers into later, unrelated test cases. */
const negativeCacheEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Read a fetched font file as a `data:` URI. */
function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error as unknown);
    reader.readAsDataURL(blob);
  });
}

interface FontFaceSource {
  readonly weight: string;
  readonly style: string;
  readonly url: string;
  readonly format?: string;
}

/**
 * Every `@font-face` rule across every readable stylesheet whose
 * `font-family` matches `familyName` (quotes/case-insensitive).
 *
 * Wrapped in a try/catch at the call site, not here: `document.styleSheets`
 * itself, or the `CSSFontFaceRule` global, could in principle be absent in
 * some embedding context — the caller's fallback contract (empty string,
 * never throw) covers that uniformly rather than duplicating the guard here.
 */
function findFontFaceSources(familyName: string): FontFaceSource[] {
  const sources: FontFaceSource[] = [];
  const needle = familyName.toLowerCase();
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin stylesheet — cannot read its rules, skip it
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const family = rule.style.getPropertyValue('font-family').replace(/['"]/g, '').trim();
      if (family.toLowerCase() !== needle) continue;
      const src = rule.style.getPropertyValue('src');
      // Review-Lauf 01 (MINOR, Linse C): only the FIRST url()/format() pair
      // in `src` is used — unlike a real `<link>`/CSS font load, there is no
      // verification that this particular URL actually resolves, and no
      // fallback to a second `url()` in the same `src` if it doesn't. A
      // multi-format `src` (e.g. woff2 + woff2 fallback) picks whichever
      // came first in the declaration, nothing more.
      const match = /url\((['"]?)([^'")]+)\1\)(?:\s*format\((['"]?)([^'")]+)\3\))?/.exec(src);
      if (!match) continue;
      let url: string;
      try {
        url = new URL(match[2]!, sheet.href ?? document.baseURI).href;
      } catch {
        continue;
      }
      sources.push({
        weight: rule.style.getPropertyValue('font-weight').trim() || '400',
        style: rule.style.getPropertyValue('font-style').trim() || 'normal',
        url,
        format: match[4],
      });
    }
  }
  return sources;
}

/**
 * Review-Lauf 01 (MINOR, Linse B): font sources used to load SEQUENTIALLY
 * (`for...await`) — with multiple `@font-face` sources for the same family
 * (e.g. regular + bold), load time summed instead of overlapping. Fetched in
 * parallel now; order in the returned CSS no longer depends on fetch speed,
 * only on `sources`' original order (`Promise.all` preserves input order).
 */
async function buildFontFaceCss(familyName: string): Promise<string> {
  let sources: FontFaceSource[];
  try {
    sources = findFontFaceSources(familyName);
  } catch {
    return '';
  }
  const parts = await Promise.all(sources.map(async (source): Promise<string | null> => {
    try {
      const res = await fetch(source.url, {signal: AbortSignal.timeout(FETCH_TIMEOUT_MS)});
      if (!res.ok) return null;
      const dataUri = await blobToDataUri(await res.blob());
      const formatDecl = source.format ? ` format('${source.format}')` : '';
      return `@font-face{font-family:'${familyName}';src:url(${dataUri})${formatDecl};` +
        `font-weight:${source.weight};font-style:${source.style};}`;
    } catch {
      // Best-effort: one unreachable/timed-out font source must not block
      // the others, nor the raster itself — see the module doc's fallback
      // contract.
      return null;
    }
  }));
  const resolved = parts.filter((part): part is string => part !== null);
  return resolved.length > 0 ? `<style>${resolved.join('')}</style>` : '';
}

/**
 * The embeddable `<style>@font-face{...}</style>` block for `familyName`. A
 * SUCCESSFUL result is computed once and cached for the module's lifetime.
 * Empty string when the consumer never declared the family, or nothing
 * could be fetched — that failure is cached only briefly (see
 * `NEGATIVE_CACHE_TTL_MS`), so a later call still retries.
 */
export function embeddedFontFaceCss(familyName: string): Promise<string> {
  let cached = cache.get(familyName);
  if (!cached) {
    cached = buildFontFaceCss(familyName).then((css) => {
      // Review-Lauf 01 (MAJOR, Linse A/B): a failed/empty result used to be
      // cached PERMANENTLY (an empty string is still a resolved Promise,
      // still kept in `cache` forever) — a transient CDN outage or 404
      // during the FIRST raster of a session meant the font never embedded
      // for the rest of it, no retry.
      //
      // Review-Lauf 02 (MAJOR, Linse B): evicting IMMEDIATELY on failure (as
      // Lauf 01 first did) instead risked a fetch STORM — every raster
      // request arriving right after the eviction starts its own fresh,
      // up-to-8s fetch attempt. A failed/empty result is now kept for a
      // short `NEGATIVE_CACHE_TTL_MS` window (near-term callers share it, no
      // stampede), then evicted so a LATER raster still retries — never
      // permanently blocked either way. Concurrent in-flight callers during
      // the initial async gap still share this one Promise, unaffected.
      if (!css) {
        const timer = setTimeout(() => {
          negativeCacheEvictionTimers.delete(familyName);
          // Only evict if this is still the SAME cached promise — guards
          // against evicting a meanwhile-succeeded retry's fresh entry.
          if (cache.get(familyName) === cached) {
            cache.delete(familyName);
          }
        }, NEGATIVE_CACHE_TTL_MS);
        negativeCacheEvictionTimers.set(familyName, timer);
      }
      return css;
    });
    cache.set(familyName, cached);
  }
  return cached;
}

/** Test-only escape hatch: clears the module-level cache between test cases
 *  that stub `document.styleSheets`/`fetch` differently. Never called from
 *  production code. */
export function __resetEmbeddedFontFaceCssCacheForTests(): void {
  cache.clear();
  negativeCacheEvictionTimers.forEach((timer) => clearTimeout(timer));
  negativeCacheEvictionTimers.clear();
}
